<template>
  <NewOrderModalShell
    :open="open"
    @close="handleModalClose"
  >
    <NewOrderModalLoadingOverlay :is-visible="isEditLoading && isEditMode" />

    <NewOrderModalHeader
      :is-edit-mode="isEditMode"
      :order-number="orderToEdit?.order_number"
      @close="$emit('close')"
    />

    <div class="flex-1 overflow-x-hidden">
      <NewOrderModalDesktopLayout
        v-if="!isMobile"
        v-bind="sharedLayoutProps"
        ref="desktopLayoutRef"
        v-on="sharedLayoutListeners"
      />

      <NewOrderModalMobileLayout
        v-else
        v-bind="sharedLayoutProps"
        ref="mobileLayoutRef"
        :active-tab="activeTab"
        :available-tabs="availableTabs"
        v-on="sharedLayoutListeners"
        @update:active-tab="activeTab = $event"
        @remove-item="(data) => removeItemFromPerson(data.personIndex, data.itemIndex)"
        @decrease-quantity="decreaseQuantity"
        @increase-quantity="increaseQuantity"
      />
    </div>
  </NewOrderModalShell>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import { type Order as OrderType } from '@/services/orderService';
import { useToast } from '@/composables/useToast';
import { useI18n } from 'vue-i18n';
import type { Extra } from './ExtrasSelector.vue';
import { usePermissions } from '@/composables/usePermissions';
import { useAuthStore } from '@/stores/auth';
import { useRestaurantSettings } from '@/composables/useRestaurantSettings';
import { useKitchenPrint } from '@/composables/useKitchenPrint';
import { useCustomerPrint } from '@/composables/useCustomerPrint';
import { useCashRegisterSession } from '@/composables/useCashRegisterSession';

import PaymentSection from './PaymentSection.vue';
import MenuItemsSelectorButtons from './MenuItemsSelectorButtons.vue';
import NewOrderModalLoadingOverlay from './NewOrderModalLoadingOverlay.vue';
import NewOrderModalHeader from './NewOrderModalHeader.vue';
import NewOrderModalShell from './NewOrderModalShell.vue';

import type { ExtendedMenuItem, OrderItemWithDetails } from '@/types/order';
import { useMenuCategories } from '@/composables/useMenuCategories';
import { useOrderItems } from '@/composables/useOrderItems';
import { useItemSelection } from '@/composables/useItemSelection';
import { useMultipleDiners } from '@/composables/useMultipleDiners';
import { useOrderCreation } from '@/composables/useOrderCreation';
import { useDataFetching } from '@/composables/useDataFetching';
import { useOperationMode } from '@/composables/useOperationMode';
import { usePosOrderShortcuts } from '@/composables/usePosOrderShortcuts';
import { useNewOrderModalLifecycle } from '@/composables/useNewOrderModalLifecycle';
import { usePersonItemsEditor } from '@/composables/usePersonItemsEditor';
import { useOrderEditHydration } from '@/composables/useOrderEditHydration';
import { usePosNextSaleReset } from '@/composables/usePosNextSaleReset';
import { useOrderItemModalAdder } from '@/composables/useOrderItemModalAdder';
import { useOrderItemDetailsAdder } from '@/composables/useOrderItemDetailsAdder';
import { useNewOrderModalUiState } from '@/composables/useNewOrderModalUiState';
import { useNewOrderModalBootstrap } from '@/composables/useNewOrderModalBootstrap';
import { useNewOrderModalFormReset } from '@/composables/useNewOrderModalFormReset';
import { useNewOrderModalPublicApi } from '@/composables/useNewOrderModalPublicApi';
import { useNewOrderModalLayoutBindings } from '@/composables/useNewOrderModalLayoutBindings';
import { useOrderPaymentState } from '@/composables/useOrderPaymentState';
import { useMenuItemDisplayHelpers } from '@/composables/useMenuItemDisplayHelpers';
import { useOrderSubmissionFlow } from '@/composables/useOrderSubmissionFlow';
import { getVariantPrice } from '@/utils/priceHelpers';

import { useOrderCalculations } from '@/composables/useOrderCalculations';
import { useOrderCustomer } from '@/composables/useOrderCustomer';
import {
  transformToOrderItemWithDetails,
  mapOrderTypeToFrontend,
  createInitialFormItem,
} from '@/utils/orderFormHelpers';

import NewOrderModalDesktopLayout from './NewOrderModalDesktopLayout.vue';
import NewOrderModalMobileLayout from './NewOrderModalMobileLayout.vue';

const props = defineProps<{
  open: boolean;
  tableId?: number | null;
  mode?: 'create' | 'edit';
  orderToEdit?: OrderType | null;
  posMode?: boolean;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'order-created', order: any): void;
  (e: 'order-updated', order: any): void;
}>();

const form = ref<{
  type: 'Dine-in' | 'Takeaway' | 'Delivery';
  tableId: number | string | null;
  customerName: string;
  notes: string;
  items: Array<{
    menu_item_id: number;
    variant_id?: number | null;
    quantity: number;
    notes?: string;
    special_instructions?: string;
    unit_price?: number;
    extras?: Extra[];
  }>;
}>({
  type: props.posMode ? 'Takeaway' : 'Dine-in',
  tableId: null as number | string | null,
  customerName: '',
  notes: '',
  items: [] as Array<{
    menu_item_id: number;
    variant_id?: number | null;
    quantity: number;
    notes?: string;
    special_instructions?: string;
    unit_price?: number;
    extras?: Extra[];
  }>
});


const topNotes = ref<string[]>([]);
const isEditMode = computed(() => {
  return (props.mode || 'create') === 'edit' && !!props.orderToEdit
});

const isEditLoading = ref(false);
const { isPosOnlyMode, isLoaded: modeLoaded } = useOperationMode();
const menuSelectorRef = ref<InstanceType<typeof MenuItemsSelectorButtons> | null>(null);
const paymentSectionRef = ref<InstanceType<typeof PaymentSection> | null>(null);
onMounted(() => {
  restaurantSettings.loadSettings();
});

const authStore = useAuthStore();
const restaurantSettings = useRestaurantSettings();

const orderCustomerComposable = useOrderCustomer(form, restaurantSettings.allowTakeawayWithoutCustomer);
const {
  selectedCustomer,
  selectedAddress,
  selectedAddressId,
  isCustomerSelectionValid,
  loadCustomerDataFromOrder,
  handleCreateCustomer,
  handleAddressSelected,
  getDeliveryAddressFromForm
} = orderCustomerComposable;

const { printKitchenTicket } = useKitchenPrint();
const { printCustomerReceipt } = useCustomerPrint();
const multipleDinersComposable = useMultipleDiners();
const {
  persons,
  activePersonIndex,
  addPerson,
  removePerson,
  updatePersonName,
  removeItemFromPerson: removeItemFromPersonFromDiners
} = multipleDinersComposable;

const { showError, showSuccess, showWarning } = useToast();
const { canProcessPayments } = usePermissions();
const { t } = useI18n();

const { activeTab, availableTabs, isMobile } = useNewOrderModalUiState({
  isPosOnlyMode,
  modeLoaded,
  t
});

const {
  removePersonWithConfirm,
  removeItemFromPerson,
  removeGroupFromPerson,
  updateItemQuantity
} = usePersonItemsEditor({
  persons,
  activePersonIndex,
  removePerson,
  removeItemFromPerson: removeItemFromPersonFromDiners,
  confirmRemovePerson: () => confirm(t('app.views.orders.modals.new_order.persons.confirm_remove'))
});

const { currentSession } = props.posMode ? useCashRegisterSession() : { currentSession: ref(null) };
const dataFetching = useDataFetching();
const { menuItems, availableTables, loading, error } = dataFetching;
const ivaRate = restaurantSettings.ivaRate;
const orderCreationComposable = useOrderCreation();
const orderCalculationsComposable = useOrderCalculations(
  persons,
  form,
  ivaRate,
  selectedCustomer,
  selectedAddress
);
const {
  ivaEnabled,
  ivaAmount,
  handleToggleIva,
  orderTotal,
  finalTotal,
  subtotal,
  deliveryFee,
  currentDiscount,
  appliedPromotions,
  promotionMessages,
  isCalculatingPromotions
} = orderCalculationsComposable;

const {
  markAsPaid,
  selectedPaymentMethod,
  cashReceived,
  isPaymentValid
} = useOrderPaymentState({
  isPosOnlyMode,
  modeLoaded,
  orderTotal
});

const {
  menuItemsByCategory,
  categoryNames,
  collapseAllCategories
} = useMenuCategories(menuItems);

const { getMenuItemName, getMenuItemCategory } = useMenuItemDisplayHelpers(menuItems);

const {
  increaseQuantity,
  decreaseQuantity,
  getItemQuantity,
  calculateItemTotal,
  isItemLocked
} = useOrderItems(computed(() => form.value.items));

const {
  selectedItem,
  selectedVariant,
  itemNotes,
  itemSpecialNote,
  itemExtras,
  showItemModal,
  showingItemOptions,
  resetItemSelection,
  selectItem: selectItemFromComposable
} = useItemSelection();

const { resetForm } = useNewOrderModalFormReset({
  form,
  posMode: computed(() => Boolean(props.posMode)),
  menuItems,
  selectedItem,
  selectedVariant,
  itemNotes,
  showItemModal,
  createInitialFormItem,
  resetMultipleDiners: () => multipleDinersComposable.reset()
});

const {
  initializeFormItemsFromMenu,
  applyOpenTableSelection,
  runInitialLoad
} = useNewOrderModalBootstrap({
  form,
  menuItems,
  tableId: computed(() => props.tableId),
  posMode: computed(() => Boolean(props.posMode)),
  fetchMenuItemsRequest: (onSuccess, onError) => dataFetching.fetchMenuItems(onSuccess, onError),
  fetchAvailableTablesRequest: (tableId, onTableSelected, onError) =>
    dataFetching.fetchAvailableTables(tableId, onTableSelected, onError),
  collapseAllCategories,
  showMenuError: () => showError(t('app.views.orders.modals.new_order.error_menu')),
  showTablesError: () => showError(t('app.views.orders.modals.new_order.error_tables'))
});

const { loadOrderIntoForm } = useOrderEditHydration({
  form,
  persons,
  activePersonIndex,
  isEditLoading,
  ivaEnabled,
  markAsPaid,
  selectedPaymentMethod,
  cashReceived,
  mapOrderTypeToFrontend,
  loadCustomerDataFromOrder,
  showError
});

const { handleModalClose, hydrateOnMountedIfEditing } = useNewOrderModalLifecycle<OrderType>({
  open: computed(() => props.open),
  isEditMode,
  orderToEdit: computed(() => props.orderToEdit),
  menuItemsCount: computed(() => menuItems.value.length),
  formItemsCount: computed(() => form.value.items.length),
  resetForm,
  initializeFormItemsFromMenu,
  applyOpenTableSelection,
  loadOrderIntoForm,
  emitClose: () => emit('close')
});

const isPosMode = computed(() => Boolean(props.posMode));
const isModalOpen = computed(() => props.open);

const selectedItems = computed<OrderItemWithDetails[]>(() => {
  return form.value.items
    .filter(item => item.quantity > 0)
    .map(item => transformToOrderItemWithDetails(item, menuItems.value));
});

const hasItems = computed(() => {
  return persons.value.some(person => person.items.length > 0);
});

usePosOrderShortcuts({
  enabled: isPosMode,
  isOpen: isModalOpen,
  markAsPaid,
  selectedPaymentMethod,
  hasItems,
  isPaymentValid,
  focusCashInput: () => {
    if (paymentSectionRef.value) {
      paymentSectionRef.value.focusCashInput();
    }
  },
  createOrder: () => {
    void createOrder();
  }
});

const { addItemWithDetails } = useOrderItemDetailsAdder({
  persons,
  activePersonIndex,
  selectedItem,
  selectedVariant,
  itemNotes,
  itemSpecialNote,
  itemExtras,
  showItemModal,
  getVariantPrice
});

async function createOrder() {
  await submitOrder();
}

const { resetFormForNextSale } = usePosNextSaleReset({
  form,
  posMode: computed(() => Boolean(props.posMode)),
  persons,
  activePersonIndex,
  isPosOnlyMode,
  markAsPaid,
  selectedPaymentMethod,
  cashReceived,
  resetItemSelection,
  activeTab,
  collapseAllCategories,
  menuSelectorRef,
  paymentSectionRef
});

const { submitOrder } = useOrderSubmissionFlow({
  form,
  persons,
  isEditMode,
  orderToEdit: computed(() => props.orderToEdit),
  posMode: computed(() => Boolean(props.posMode)),
  currentSession,
  markAsPaid,
  selectedPaymentMethod,
  cashReceived,
  ivaEnabled,
  ivaRate,
  deliveryFee,
  orderTotal,
  currentDiscount,
  appliedPromotions,
  selectedCustomer,
  selectedAddress,
  allowDineInWithoutTable: computed(() => authStore.restaurant?.allow_dine_in_without_table),
  allowTakeawayWithoutCustomer: computed(() => authStore.restaurant?.allow_takeaway_without_customer),
  orderCreationComposable,
  t,
  showError,
  showSuccess,
  showWarning,
  ensureRestaurantLoaded: async () => {
    if (!authStore.restaurant) {
      await authStore.loadRestaurant();
    }
  },
  isKitchenPrintEnabled: () => Boolean(authStore.restaurant?.kitchen_print_enabled),
  isCustomerPrintEnabled: () => Boolean(authStore.restaurant?.customer_print_enabled),
  printKitchenTicket,
  printCustomerReceipt,
  waitForUi: async () => {
    await nextTick();
  },
  emitOrderUpdated: (order) => emit('order-updated', order),
  emitOrderCreated: (order) => emit('order-created', order),
  emitClose: () => emit('close'),
  resetForNextSale: resetFormForNextSale
});

async function selectItem(menuItem: ExtendedMenuItem) {
  selectItemFromComposable(menuItem, isMobile.value);
}

const { handleAddItemFromModal } = useOrderItemModalAdder({
  persons,
  activePersonIndex,
  selectedItem,
  showingItemOptions
});

const { sharedLayoutProps, sharedLayoutListeners } = useNewOrderModalLayoutBindings({
  form,
  showingItemOptions,
  selectedItem,
  topNotes,
  persons,
  activePersonIndex,
  isEditMode,
  availableTables,
  loading,
  error,
  categoryNames,
  menuItemsByCategory,
  selectedItems,
  ivaEnabled,
  ivaRate,
  ivaAmount,
  subtotal,
  deliveryFee,
  currentDiscount,
  finalTotal,
  appliedPromotions,
  promotionMessages,
  isCalculatingPromotions,
  selectedCustomer,
  selectedAddress,
  selectedAddressId,
  isCustomerSelectionValid,
  markAsPaid,
  selectedPaymentMethod,
  cashReceived,
  isPaymentValid,
  hasItems,
  canProcessPayments,
  isPosOnlyMode,
  modeLoaded,
  getMenuItemName,
  getMenuItemCategory,
  getItemQuantity,
  calculateItemTotal,
  isItemLocked,
  getVariantPrice,
  getDeliveryAddressFromForm,
  handleAddItemFromModal,
  handleCreateCustomer,
  handleAddressSelected,
  addPerson,
  removePersonWithConfirm,
  updatePersonName,
  removeGroupFromPerson,
  updateItemQuantity,
  selectItem,
  handleToggleIva,
  createOrder,
  restaurantSettings
});

const publicApi = useNewOrderModalPublicApi({
  selectItem,
  addItemWithDetails,
  increaseQuantity,
  decreaseQuantity,
  getItemQuantity,
  createOrder,
  calculateItemTotal
});

defineExpose(publicApi);

onMounted(async () => {
  await runInitialLoad(hydrateOnMountedIfEditing);
});

</script>
